import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import { Label } from "../../Components/ui/label";
import { FourDots } from "../../Components/FourDots";
import { AuthFooter } from "../../Components/AuthFooter";
import measureLogo from "/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png";
import { CHALLENGES } from "../../utils/cognito";
import { useDispatch } from "react-redux";
import { api } from "../../../redux/reducers/apiSlice";
import { setToken } from "../../../redux/reducers/authSlice";
import * as Sentry from "@sentry/react";
import { configureAmplifyPool } from "../../utils/amplifyPools";

export type LoginEntry = "member" | "measure";

type LoginProps = {
  /** `measure` = dedicated entry for platform operators (internal dashboard). */
  entry?: LoginEntry;
};

export default function Login({ entry = "member" }: LoginProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [operatorPoolConfigError, setOperatorPoolConfigError] = useState<string | null>(null);

  useEffect(() => {
    if (entry === "measure") {
      try {
        configureAmplifyPool("measureOperator");
        setOperatorPoolConfigError(null);
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "Measure operator Cognito is not configured";
        setOperatorPoolConfigError(message);
      }
    } else {
      configureAmplifyPool("member");
      setOperatorPoolConfigError(null);
    }
  }, [entry]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      if (entry === "measure") {
        configureAmplifyPool("measureOperator");
      } else {
        configureAmplifyPool("member");
      }

      const user = await Auth.signIn(email, password);
      
      if (user.challengeName === CHALLENGES.NEW_PASSWORD_REQUIRED) {
        navigate("/new-password-required", {
          state: {
            email: email,
            ...(entry === "measure" ? { cognitoPool: "measureOperator" as const } : {}),
          },
        });
        return;
      }

      dispatch(api.util.resetApiState());
      dispatch(setToken({ token: user.signInUserSession.idToken.jwtToken }));
      setLoading(false);
      const attributes = user.signInUserSession?.idToken?.payload;
      const storageKey = `${attributes.email}-loginFlag`;
      const loginFlagValue = localStorage.getItem(storageKey);
      if (loginFlagValue) {
        const newLoginFlagValue = parseInt(loginFlagValue) + 1;
        localStorage.setItem(storageKey, newLoginFlagValue.toString());
      } else {
        localStorage.setItem(storageKey, "1");
      }
      // Don't navigate - let App.tsx handle route switching based on isAuthenticated
      // This ensures profile is loaded before showing dashboard
    } catch (error: any) {
      Sentry.captureException(error);
      setLoading(false);

      if (new Error(error)?.message.includes("New password required")) {
        navigate("/new-password-required", {
          state: {
            email: email,
            ...(entry === "measure" ? { cognitoPool: "measureOperator" as const } : {}),
          },
        });
        return;
      }

      if (error.code === "UserNotConfirmedException") {
        toast.error("User is not confirmed");
        await Auth.resendSignUp(email);
        navigate("/confirm-signup", {
          state: {
            email: email,
            password: password,
          },
        });
      } else {
        const errorMessage = new Error(error)?.message;
        if (errorMessage) {
          if (errorMessage.includes(":")) {
            const messageWithoutErrorCode = errorMessage.split(":")[1].trim();
            toast.error(messageWithoutErrorCode);
          } else {
            toast.error(errorMessage);
          }
        } else {
          toast.error("Something unexpected happened");
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <Link to="/" className="mb-8">
              <img
                src={measureLogo}
                alt="Measure Ignite"
                className="h-25 w-auto"
              />
            </Link>
          </div>

          {entry === "measure" && (
            <p className="mb-4 text-center text-sm text-gray-600">
              Sign in with your Measure platform operator account. This is separate from the member experience.
            </p>
          )}

          {entry === "measure" && operatorPoolConfigError && (
            <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-center text-sm text-red-800">
              {operatorPoolConfigError}
            </p>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                maxLength={30}
              />
              <div className="flex justify-end pt-1">
                <Link
                  to={entry === "measure" ? "/measure/forgot-password" : "/forgot-password"}
                  className="text-sm text-orange-600 hover:text-orange-700"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={loading || (entry === "measure" && !!operatorPoolConfigError)}
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            {entry === "measure" ? (
              <>
                Looking for the member app?{" "}
                <Link
                  to="/login"
                  className="text-orange-600 hover:text-orange-700 font-semibold"
                >
                  Member sign in
                </Link>
              </>
            ) : (
              <>
                Not a Member?{" "}
                <Link
                  to="/signup"
                  className="text-orange-600 hover:text-orange-700 font-semibold"
                >
                  Join now
                </Link>
              </>
            )}
          </p>

          <AuthFooter />
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-center items-center">
          <div className="max-w-lg text-center mb-12">
            <h2 className="text-2xl font-semibold mb-4">
              {entry === "measure"
                ? "Internal dashboard for Measure operators."
                : "Track your impact. Share your work. Find your village."}
            </h2>
            <p className="text-gray-700 text-sm">
              {entry === "measure"
                ? "Platform health, organizations, and reporting — restricted access."
                : "Ignite helps you organize impact data, generate reports, and connect with your community."}
            </p>
          </div>
          <FourDots size="2xl" opacity={0.7} />
        </div>
      </div>
    </div>
  );
}
